import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquare, X, Send, Sparkles, Loader2, Briefcase, FileDown, Mail, Layers, FileText } from "lucide-react";
import { cvList } from "../../data/content";
import { trackCvDownload } from "./CVMenu";
import { scrollToId } from "./Nav";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const detectLang = () => {
  const nav = (typeof navigator !== "undefined" && (navigator.language || navigator.userLanguage)) || "fr";
  return nav.toLowerCase().startsWith("fr") ? "fr" : "en";
};

const T = {
  fr: {
    btnOpen: "Assistant IA",
    btnClose: "Fermer",
    headerTitle: "Assistant d'Andrew",
    headerSub: "Répond à partir de son profil",
    welcome:
      "Bonjour 👋 Je suis l'assistant d'Andrew Picornell. Posez-moi vos questions sur son profil, son parcours ou sa recherche d'alternance.",
    suggestions: [
      "Quel type d'alternance recherche Andrew ?",
      "Quelles sont ses expériences clés ?",
      "En quoi son profil est-il pertinent pour mon poste ?",
    ],
    actions: { exp: "Ses expériences", skills: "Ses compétences", cv: "Télécharger le bon CV", contact: "Le contacter" },
    placeholder: "Écrivez votre question…",
    error: "Désolé, une erreur est survenue. Vous pouvez contacter Andrew directement via le formulaire de contact ou par e-mail.",
    footer: "Réponses générées par IA à partir du profil d'Andrew.",
    recommended: "Recommandé",
  },
  en: {
    btnOpen: "AI Assistant",
    btnClose: "Close",
    headerTitle: "Andrew's Assistant",
    headerSub: "Answers based on his profile",
    welcome:
      "Hi 👋 I'm Andrew Picornell's assistant. Ask me anything about his profile, background or his work-study (alternance) search.",
    suggestions: [
      "What kind of apprenticeship is Andrew looking for?",
      "What are his key experiences?",
      "Why is his profile relevant for my role?",
    ],
    actions: { exp: "His experience", skills: "His skills", cv: "Download the right CV", contact: "Contact him" },
    placeholder: "Type your question…",
    error: "Sorry, something went wrong. You can reach Andrew directly through the contact form or by e-mail.",
    footer: "AI-generated answers based on Andrew's profile.",
    recommended: "Recommended",
  },
};

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [lang, setLang] = useState(detectLang);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [showCvs, setShowCvs] = useState(false);
  const scrollRef = useRef(null);

  const t = T[lang];
  const welcomeMsg = { role: "assistant", content: t.welcome };
  const isFresh = messages.length === 0;

  const goto = (id) => { setOpen(false); setShowCvs(false); setTimeout(() => scrollToId(id), 30); };

  const switchLang = (next) => {
    if (next === lang) return;
    setLang(next);
    setMessages([]);
    setSessionId(null);
    setShowCvs(false);
  };

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading, open]);

  const send = async (text) => {
    const question = (text ?? input).trim();
    if (!question || loading) return;
    const history = messages.map((m) => ({ role: m.role, content: m.content }));
    setMessages((prev) => [...prev, { role: "user", content: question }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/chat`, { message: question, history, session_id: sessionId, lang });
      setSessionId(data.session_id);
      setMessages((prev) => [...prev, { role: "assistant", content: data.reply }]);
    } catch (err) {
      setMessages((prev) => [...prev, { role: "assistant", content: t.error }]);
    } finally {
      setLoading(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  return (
    <>
      {/* Floating button */}
      <motion.button
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 2, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-5 right-5 z-[60] inline-flex items-center gap-2 bg-cyan text-[#05070A] font-medium pl-4 pr-5 py-3 rounded-full shadow-lg shadow-cyan/20 hover:bg-white transition-colors duration-300"
        data-testid="chat-toggle"
        aria-label={open ? t.btnClose : t.btnOpen}
      >
        {open ? <X size={18} /> : <Sparkles size={18} />}
        <span className="text-sm hidden sm:block">{open ? t.btnClose : t.btnOpen}</span>
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-20 right-5 z-[60] w-[calc(100vw-2.5rem)] sm:w-[380px] h-[540px] max-h-[75vh] flex flex-col glass border border-white/10 rounded-3xl overflow-hidden shadow-2xl shadow-black/60"
            data-testid="chat-panel"
          >
            {/* Header */}
            <div className="flex items-center gap-3 px-5 py-4 border-b border-white/10 bg-[#0B101A]/80">
              <span className="h-9 w-9 inline-flex items-center justify-center rounded-full bg-cyan/10 border border-cyan/30 text-cyan"><MessageSquare size={16} /></span>
              <div className="min-w-0 flex-1">
                <p className="font-display font-medium text-sm text-white leading-tight">{t.headerTitle}</p>
                <p className="text-[11px] text-[var(--text-2)]">{t.headerSub}</p>
              </div>
              {/* Language toggle */}
              <div className="flex items-center rounded-full border border-white/10 overflow-hidden text-[11px] font-medium shrink-0" data-testid="chat-lang-toggle">
                {["fr", "en"].map((l) => (
                  <button
                    key={l}
                    onClick={() => switchLang(l)}
                    className={`px-2.5 py-1 uppercase transition-colors ${lang === l ? "bg-cyan text-[#05070A]" : "text-[var(--text-2)] hover:text-white"}`}
                    data-testid={`chat-lang-${l}`}
                    aria-label={l === "fr" ? "Français" : "English"}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="chat-messages">
              {[welcomeMsg, ...messages].map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] text-sm leading-relaxed rounded-2xl px-4 py-2.5 ${m.role === "user" ? "bg-cyan text-[#05070A] rounded-br-sm" : "bg-white/5 text-white/90 border border-white/10 rounded-bl-sm"}`}
                    data-testid={`chat-msg-${m.role}`}
                  >
                    {m.content}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-white/5 border border-white/10 rounded-2xl rounded-bl-sm px-4 py-3 text-cyan">
                    <Loader2 size={16} className="animate-spin" />
                  </div>
                </div>
              )}
              {isFresh && !loading && (
                <div className="pt-2 space-y-2" data-testid="chat-suggestions">
                  {t.suggestions.map((s, i) => (
                    <button
                      key={i}
                      onClick={() => send(s)}
                      className="block w-full text-left text-sm text-[var(--text-2)] border border-white/10 rounded-xl px-3.5 py-2.5 hover:border-cyan/50 hover:text-white transition-colors duration-200"
                      data-testid={`chat-suggestion-${i}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Actions guidées */}
            <div className="px-3 pt-3 border-t border-white/10">
              <div className="flex flex-wrap gap-1.5">
                <button onClick={() => goto("experiences")} className="inline-flex items-center gap-1.5 text-xs text-[var(--text-2)] border border-white/10 rounded-full px-3 py-1.5 hover:border-cyan/50 hover:text-white transition-colors" data-testid="chat-action-experiences">
                  <Briefcase size={13} className="text-cyan" /> {t.actions.exp}
                </button>
                <button onClick={() => goto("competences")} className="inline-flex items-center gap-1.5 text-xs text-[var(--text-2)] border border-white/10 rounded-full px-3 py-1.5 hover:border-cyan/50 hover:text-white transition-colors" data-testid="chat-action-competences">
                  <Layers size={13} className="text-cyan" /> {t.actions.skills}
                </button>
                <button onClick={() => setShowCvs((s) => !s)} className={`inline-flex items-center gap-1.5 text-xs rounded-full px-3 py-1.5 border transition-colors ${showCvs ? "border-cyan/60 text-white" : "border-white/10 text-[var(--text-2)] hover:border-cyan/50 hover:text-white"}`} data-testid="chat-action-cv">
                  <FileDown size={13} className="text-cyan" /> {t.actions.cv}
                </button>
                <button onClick={() => goto("contact")} className="inline-flex items-center gap-1.5 text-xs text-[var(--text-2)] border border-white/10 rounded-full px-3 py-1.5 hover:border-cyan/50 hover:text-white transition-colors" data-testid="chat-action-contact">
                  <Mail size={13} className="text-cyan" /> {t.actions.contact}
                </button>
              </div>
              <AnimatePresence>
                {showCvs && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                    data-testid="chat-cv-list"
                  >
                    <div className="mt-2 space-y-1.5">
                      {cvList.map((cv, i) => (
                        <a
                          key={i}
                          href={cv.file}
                          download={cv.download}
                          onClick={() => { trackCvDownload(cv.slug); setShowCvs(false); }}
                          className="flex items-center gap-2.5 text-xs text-white/90 bg-white/5 border border-white/10 rounded-lg px-3 py-2 hover:border-cyan/50 transition-colors"
                          data-testid={`chat-cv-${i}`}
                        >
                          <FileText size={13} className="text-cyan shrink-0" /> {cv.axe}
                          {cv.recommended && <span className="ml-auto text-[9px] uppercase tracking-wider text-cyan">{t.recommended}</span>}
                        </a>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Input */}
            <div className="p-3 border-t border-white/10 bg-[#0B101A]/80">
              <div className="flex items-end gap-2">
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={onKey}
                  rows={1}
                  placeholder={t.placeholder}
                  className="flex-1 resize-none bg-white/5 border border-white/10 rounded-2xl px-4 py-2.5 text-sm text-white placeholder-[var(--muted)] focus:border-cyan/50 focus:outline-none transition-colors max-h-24"
                  data-testid="chat-input"
                />
                <button
                  onClick={() => send()}
                  disabled={loading || !input.trim()}
                  className="h-10 w-10 shrink-0 inline-flex items-center justify-center bg-cyan text-[#05070A] rounded-full hover:bg-white transition-colors disabled:opacity-40"
                  data-testid="chat-send"
                  aria-label="Envoyer"
                >
                  <Send size={16} />
                </button>
              </div>
              <p className="text-[10px] text-[var(--muted)] text-center mt-2">{t.footer}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
